import { useEffect, useState } from 'react';
import { getP4PDayBadges } from '../lib/p4pDay';

/**
 * Sized for TWO lines, not one.
 *
 * These were 34x40 while the flag held only the sponsor's word. Adding the fan's number
 * underneath left the stack taller than the label area, and the label box clips what overflows.
 *
 * The geometry is exact: label area (HEIGHT - NOTCH) minus the reserved top inset leaves 29px
 * for a 22px stack, and the name's centre lands on the flag body's centre. Changing any one of
 * these numbers without re-checking that sum puts the number back under the clip.
 */
const WIDTH = 42;
const HEIGHT = 50;
/** Depth of the downward V at the foot of the pennant. */
const NOTCH = 10;
/**
 * Half the outline width, so a 1px stroke centred on the path sits entirely inside the SVG
 * viewport instead of being clipped in half at the edges.
 */
const INSET = 0.5;
/** The fan's badge number, under the name. Quieter than the sponsor's word above it. */
const NUMBER_SIZE = 11;
const NUMBER_GAP = 0;

const PENNANT_POINTS = [
  `${INSET},${INSET}`,
  `${WIDTH - INSET},${INSET}`,
  `${WIDTH - INSET},${HEIGHT - NOTCH}`,
  `${WIDTH / 2},${HEIGHT - INSET}`,
  `${INSET},${HEIGHT - NOTCH}`
].join(' ');

/**
 * Picks black or white for the label, whichever stays readable on the given background.
 *
 * The sponsor sets `colorHex` and is expected to change it more than once, so a hardcoded white
 * label would eventually land on a pale colour and vanish. Uses the WCAG relative-luminance
 * threshold rather than a naive brightness average, which misjudges saturated blues and reds.
 *
 * Kept in sync with the mobile component of the same name.
 */
function readableLabelColor(hex) {
  const raw = String(hex).replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;

  const channel = (offset) => {
    const srgb = parseInt(full.slice(offset, offset + 2), 16) / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
  };

  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.45 ? '#111111' : '#FFFFFF';
}

/**
 * Outline for the pennant.
 *
 * Tier C is white, and a white flag on a white card is no flag at all. A pale fill gets a faint
 * neutral edge so the shape still reads; a dark one is stroked in its own colour, which keeps
 * the silhouette exactly the size it was before the outline existed.
 *
 * Pale is decided by the same luminance test as the label, so the two can never disagree about
 * which side of the line a sponsor's new colour falls on.
 */
function outlineColor(hex) {
  return readableLabelColor(hex) === '#111111' ? 'rgba(17, 17, 17, 0.25)' : hex;
}

/**
 * The P4P Day gift badge: a coloured pennant shown beside the team name for the first 2,000
 * fans. Booth staff read it to decide which prize the fan receives.
 *
 * Takes the fan's gift LETTER and resolves the name and colour from the campaign config, so a
 * sponsor rename or recolour reaches badges that were already claimed. The number beneath the
 * name is the one exception: it is the fan's own, frozen at claim time and passed in by the
 * caller. Renders nothing of its own — no hardcoded text, no hardcoded palette — and nothing at
 * all for a fan without a badge.
 *
 * Mirrors playaz4playaz-mobile/src/components/P4PDayGiftBadge.tsx; keep the two in step.
 */
const P4PDayGiftBadge = ({ giftColor, badgeNo, style, onClick }) => {
  const [badge, setBadge] = useState(null);

  useEffect(() => {
    if (!giftColor) return undefined;

    let active = true;
    getP4PDayBadges().then((badges) => {
      if (!active) return;
      setBadge((badges || []).find((b) => b.color === giftColor) || null);
    });

    return () => {
      active = false;
    };
  }, [giftColor]);

  if (!giftColor || !badge?.name || !badge?.colorHex) return null;

  // Interactive only where a caller asks for it. The admin panel renders this to show a
  // colleague which badge SOMEONE ELSE holds, and tapping that must not offer up a fan modal.
  const interactive = !!onClick;

  // Which badge of its colour the fan holds. Absent for anyone enrolled before the field
  // existed, so the pennant has to read correctly without it rather than printing "#undefined".
  const hasNumber = Number.isFinite(badgeNo);

  return (
    <span
      role={interactive ? 'button' : 'img'}
      tabIndex={interactive ? 0 : undefined}
      aria-label={hasNumber ? `${badge.name} #${badgeNo}` : badge.name}
      title={badge.name}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      style={{
        position: 'relative',
        display: 'inline-block',
        width: WIDTH,
        height: HEIGHT,
        flex: '0 0 auto',
        cursor: interactive ? 'pointer' : undefined,
        ...style
      }}
    >
      <svg width={WIDTH} height={HEIGHT} style={{ display: 'block' }}>
        <polygon
          points={PENNANT_POINTS}
          fill={badge.colorHex}
          stroke={outlineColor(badge.colorHex)}
          strokeWidth="1"
          strokeLinejoin="round"
        />
      </svg>

      <span
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          // Bottom inset keeps the text off the V, so it stays optically centred in the flag.
          bottom: NOTCH,
          // Reserving the number's height ABOVE the stack, rather than letting a two-line block
          // centre itself, keeps the name exactly where it sat when it was alone: the padding
          // pushes the pair down by half the number's height, which is precisely what adding a
          // second line took off. Without it the sponsor's word rides up near the top edge.
          // Side padding set per-edge, not with the shorthand: a `padding` shorthand after
          // this would reset the top inset above back to zero.
          paddingLeft: 2,
          paddingRight: 2,
          paddingTop: hasNumber ? NUMBER_SIZE + NUMBER_GAP : 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: readableLabelColor(badge.colorHex),
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden'
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>{badge.name}</span>

        {hasNumber && (
          <span
            style={{
              marginTop: NUMBER_GAP,
              fontSize: NUMBER_SIZE,
              fontWeight: 600,
              lineHeight: 1,
              // Slightly under the name, which is the sponsor's word and should stay the
              // loudest thing on the flag. Same ink, so it works on any fill the label does.
              // opacity: 0.8,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            #{badgeNo}
          </span>
        )}
      </span>
    </span>
  );
};

export default P4PDayGiftBadge;
